import React, { useState } from 'react';
import { Box, Typography, Popover } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FIELD_CATEGORIES } from './fieldTypes';
import { NODE_UI } from '../../../NodeUI/nodeUiStyles';

export function FieldTypeGrid({ categories, onSelect }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
      }}
    >
      {categories.map((cat) => (
        <Box key={cat.label} sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 120 }}>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
              px: '6px',
              pb: '2px',
              userSelect: 'none',
            }}
          >
            {cat.label}
          </Typography>
          {cat.types.map((t) => {
            const Icon = t.icon;
            return (
              <Box
                key={t.value}
                onClick={() => onSelect(t.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  px: '6px',
                  py: '4px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  transition: 'background-color 0.1s ease',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Icon sx={{ width: 15, height: 15, color: 'text.secondary', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap' }}>
                  {t.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

export const fieldTypePopoverProps = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
  transformOrigin: { vertical: 'top', horizontal: 'center' },
  slotProps: {
    paper: {
      sx: {
        borderRadius: '10px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        mt: '4px',
        p: 1.5,
      },
    },
  },
};

export default function AddFieldButton({ onSelect, position, palette, alwaysVisible = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (type) => {
    onSelect(type, position);
    handleClose();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        py: alwaysVisible ? '4px' : '3px',
      }}
    >
      <Box
        onClick={handleClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          px: '10px',
          py: '4px',
          borderRadius: '100px',
          border: `1px dashed ${palette.border}`,
          backgroundColor: palette.surface,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          userSelect: 'none',
          '&:hover': {
            borderColor: palette.textSecondary,
            borderStyle: 'solid',
            backgroundColor: palette.surfaceSubtle,
            boxShadow: NODE_UI.shadow,
          },
        }}
      >
        <AddIcon sx={{ width: 15, height: 15, color: palette.textSecondary }} />
        <Typography sx={{ fontSize: '12px', fontWeight: 500, color: palette.textSecondary }}>
          Add field
        </Typography>
      </Box>

      <Popover open={open} anchorEl={anchorEl} onClose={handleClose} {...fieldTypePopoverProps}>
        <FieldTypeGrid categories={FIELD_CATEGORIES} onSelect={handleSelect} />
      </Popover>
    </Box>
  );
}
